import { Center, Paper, Stack, Table, Text } from "@mantine/core";
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import ReusableSkeleton from "@components/ReusableSkeleton/ReusableSkeleton";
import { EmptyState } from "./EmptyState";
import { ErrorState } from "./ErrorState";
import { useClients } from "@utils/hooks/useClients";
import { useStatusStore } from "@utils/stores/useStatusStore";
import "./ClientListTable.scss";
import { notifications } from "@mantine/notifications";
import ArchiveModal from "@components/Modals/ArchiveModal/ArchiveModal";
import { useClientNavigation } from "@/utils/hooks/useClientNavigation";
import BulkActionsToolbar from "./BulkActionsToolbar";
import { useClientSelectionStore } from "@utils/stores/useClientSelectionStore";
import ClientRow from "./ClientRow";
import TableHeader from "./TableHeader";
import PaginationControls from "./PaginationControls";
import type { ClientListTableProps } from "./types";

const ClientListTable = ({ onInviteClick }: ClientListTableProps) => {
  const [archivedModalOpen, setArchivedModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  // Use Zustand store for selection state
  // Subscribe to selectedClientIds directly to trigger re-renders on selection changes
  const selectedClientIds = useClientSelectionStore(
    (state) => state.selectedClientIds
  );
  const {
    toggleClient,
    selectAll,
    clearSelection,
    isAllSelected,
    isIndeterminate,
  } = useClientSelectionStore();

  // Get computed values from store
  const selectedCount = selectedClientIds.size;
  const selectedClientIdsArray = Array.from(selectedClientIds);

  const {
    clickedStatus,
    clickedAssignees,
    clickedTags,
    clickedOrganizerStatus,
    sortBy,
    order,
    limit,
    setLimit,
    searchQuery,
    showArchivedClients,
    selectedTaxYear,
    setAvailableTaxYears,
  } = useStatusStore();

  const {
    clients: clientsData,
    totalClients,
    totalPages,
    availableTaxYears,
    isLoadingClients: isClientsLoading,
    isErrorClients: isClientsError,
    errorClients,
    refetch: refetchClients,
  } = useClients({
    limit: limit,
    page: currentPage,
    status: clickedStatus as string,
    assignedTo: clickedAssignees,
    tagIds: clickedTags,
    organizerStatus: clickedOrganizerStatus as string,
    sortBy: sortBy,
    order: order,
    search: searchQuery,
    includeArchived: showArchivedClients,
    taxYear: selectedTaxYear,
  });

  const { navigateToClient } = useClientNavigation();

  // Update availableTaxYears in store when received
  useEffect(() => {
    if (availableTaxYears && availableTaxYears.length > 0) {
      setAvailableTaxYears(availableTaxYears);
    }
  }, [availableTaxYears, setAvailableTaxYears]);

  // Consolidate loading and error handling
  useEffect(() => {
    setIsLoading(isClientsLoading);
    if (isClientsError) {
      notifications.show({
        title: "Error",
        message: "Failed to fetch clients. Please try again.",
        color: "red",
      });
    }
  }, [isClientsLoading, isClientsError]);

  // Reset page to 1 when filters change (but not when limit changes)
  useEffect(() => {
    setCurrentPage(1);
    // Clear selections when filters change
    startTransition(() => {
      clearSelection();
    });
  }, [
    clickedStatus,
    clickedAssignees,
    clickedTags,
    clickedOrganizerStatus,
    sortBy,
    order,
    searchQuery,
    showArchivedClients,
    clearSelection,
  ]);

  // Clear selections when page changes
  useEffect(() => {
    startTransition(() => {
      clearSelection();
    });
  }, [currentPage, clearSelection]);

  // Handle limit changes separately to avoid infinite loops
  useEffect(() => {
    // Only reset to page 1 if current page would be out of bounds
    if (totalPages && currentPage > parseInt(totalPages)) {
      setCurrentPage(1);
    }
  }, [limit, totalPages, currentPage]);

  const handleClientClick = useCallback(
    (e: { stopPropagation: () => void }, clientId: any) => {
      e.stopPropagation();

      // Don't attempt to find client if data is still loading
      if (!clientsData || clientsData.length === 0) {
        return;
      }

      // Find client by clientId or userId (backend may use either)
      const client = clientsData.find(
        (c) => (c.clientId || c.userId) === clientId
      );
      if (!client) {
        return;
      }

      navigateToClient(client, {
        onArchived: () => setArchivedModalOpen(true),
        taxYear: selectedTaxYear,
      });
    },
    [clientsData, navigateToClient, selectedTaxYear]
  );

  const handleSelectClient = useCallback(
    (clientId: string, selected: boolean) => {
      toggleClient(clientId, selected);
    },
    [toggleClient]
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked && clientsData && clientsData.length > 0) {
        // Filter out any clients without userId or clientId
        const allClientIds = clientsData
          .map((client: any) => client?.clientId || client?.userId)
          .filter((id: any) => id != null);
        selectAll(allClientIds);
      } else {
        clearSelection();
      }
    },
    [clientsData, selectAll, clearSelection]
  );

  // Memoize clients with valid IDs to avoid repeated filtering
  const clientsWithIds = useMemo(() => {
    if (!clientsData) return [];
    return clientsData.filter(
      (client: any) => (client?.clientId || client?.userId) != null
    );
  }, [clientsData]);

  // Get available client IDs for checkbox state calculations
  const availableClientIds = useMemo(() => {
    return clientsWithIds.map(
      (client: any) => client.clientId || client.userId
    );
  }, [clientsWithIds]);

  // Use store methods for checkbox states
  const allSelected = isAllSelected(availableClientIds);
  const indeterminate = isIndeterminate(availableClientIds);

  // Memoize client rows to prevent unnecessary re-renders
  // Use isSelected from store for O(1) lookups
  // Depend on selectedClientIds so rows re-render when selection changes
  const rows = useMemo(() => {
    return clientsWithIds.map((client: any) => {
      const clientIdentifier = client.clientId || client.userId;
      return (
        <ClientRow
          key={clientIdentifier}
          client={client}
          handleClientClick={handleClientClick}
          isSelected={selectedClientIds.has(clientIdentifier)}
          onSelect={handleSelectClient}
        />
      );
    });
  }, [
    clientsWithIds,
    handleClientClick,
    selectedClientIds,
    handleSelectClient,
  ]);

  return (
    <ReusableSkeleton
      isLoading={isClientsLoading}
      layout="stack"
      layoutProps={{ items: 5 }}
    >
      <Paper p="lg" withBorder className="client-list-paper">
        {/* Bulk Actions Toolbar - shown when clients are selected */}
        {selectedCount > 0 && (
          <div style={{ marginBottom: "1rem" }}>
            <BulkActionsToolbar
              selectedCount={selectedCount}
              selectedClientIds={selectedClientIdsArray}
              onClearSelection={clearSelection}
              onUpdateComplete={clearSelection}
              clients={clientsData || []}
            />
          </div>
        )}

        {/* Pagination Controls */}
        {!isClientsLoading && (clientsData?.length ?? 0) > 0 && (
          <PaginationControls
            totalClients={totalClients}
            limit={limit}
            setLimit={setLimit}
            totalPages={totalPages}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        )}

        {/* Show loading state, empty state, or table */}
        {isLoading ? (
          <Center p="xl">
            <Stack align="center" gap="sm">
              <Text size="sm" c="dimmed">
                Loading clients...
              </Text>
            </Stack>
          </Center>
        ) : (
          <Table
            horizontalSpacing="md"
            verticalSpacing="md"
            withRowBorders={true}
          >
            <TableHeader
              allSelected={allSelected}
              indeterminate={indeterminate}
              onSelectAll={handleSelectAll}
            />
            {isClientsError ? (
              <Table.Tbody>
                <ErrorState
                  onRetry={() => refetchClients()}
                  error={errorClients}
                />
              </Table.Tbody>
            ) : (clientsData?.length ?? 0) === 0 ? (
              <Table.Tbody>
                <EmptyState
                  onInviteClick={onInviteClick}
                  hasFiltersApplied={!!clickedAssignees?.length}
                  searchQuery={searchQuery}
                  statusFilter={clickedStatus}
                  showArchivedClients={showArchivedClients}
                />
              </Table.Tbody>
            ) : (
              <Table.Tbody>{rows}</Table.Tbody>
            )}
          </Table>
        )}
      </Paper>
      <ArchiveModal
        opened={archivedModalOpen}
        onClose={() => setArchivedModalOpen(false)}
      />
    </ReusableSkeleton>
  );
};

export default ClientListTable;
